import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import axios from 'axios';
import API_ENDPOINTS from '../../config/apiEndpoints';
import { FiX, FiRefreshCw, FiList, FiClock, FiCheck } from 'react-icons/fi';

const StatusAlert = ({ isOpen, onClose, type = 'success', title, message }) => {
  if (!isOpen) return null;
  const isSuccess = type === 'success';

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm p-8 flex flex-col items-center text-center animate-in fade-in zoom-in duration-200">
          <div className={`w-24 h-24 rounded-full border-4 flex items-center justify-center mb-6 ${
            isSuccess ? 'border-green-100 text-green-500' : 'border-red-100 text-red-500'
          }`}>
            <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
              isSuccess ? 'border-2 border-green-500' : 'border-2 border-red-500'
            }`}>
              {isSuccess ? <FiCheck size={48} /> : <FiX size={48} />}
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">{title || (isSuccess ? 'Success' : 'Error')}</h2>
          <p className="text-gray-500 text-lg mb-8 leading-relaxed">{message}</p>
          <button
            onClick={onClose}
            className="w-24 py-2.5 bg-[#6366f1] text-white rounded-md font-semibold text-lg hover:bg-[#4f46e5] transition-colors focus:outline-none shadow-md"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const MailSettingsModal = ({ isOpen, onClose, editData = null }) => {
  const [sections, setSections] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  
  const [formData, setFormData] = useState({
    id: "0",
    section: 'Enquiry',
    userCollection: [], // Array of user objects { User_ID, User_Login }
    alternateEmails: '',
    scope: 'Global',
    sendPeriod: 'Daily',
    sendAtTime: '10:47',
    isActive: true
  });

  const TOKEN = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN;
  const NEW_BASE_URL = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
  const ustrUserId = localStorage.getItem('ustrUserId') || localStorage.getItem('userId') || localStorage.getItem('UserId') || '34594';
  const Save_DashBoard_Mail_Setting =  `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.SAVE_DASHBOARD_MAIL_SETTING}`; 

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        // Initialize with edit data
        const initialFormData = {
          id: String(editData.ID || "0"),
          section: editData.Sections || 'Enquiry',
          userCollection: [],
          alternateEmails: editData.EmailIDs || '',
          scope: editData.Scope || 'Global',
          sendPeriod: editData.SendAtPeriod || 'Daily',
          sendAtTime: editData.SendAtTime || '10:47',
          isActive: editData.IsActive !== undefined ? editData.IsActive : true
        };

        // Map UserCollections to objects if possible
        if (editData.UserCollections && (editData.UserCollectionsByName || editData.UserCollectionsByNAme)) {
          const ids = String(editData.UserCollections).split(',');
          const names = String(editData.UserCollectionsByName || editData.UserCollectionsByNAme).split(',');
          initialFormData.userCollection = ids.map((id, index) => ({
            User_ID: id.trim(),
            User_Login: names[index] ? names[index].trim() : id.trim()
          }));
        }

        setFormData(initialFormData);
        // Still fetch users and sections to populate selects/collections
        fetchInitialData(true);
      } else {
        // Reset for Add mode
        setFormData({
          id: "0",
          section: 'Enquiry',
          userCollection: [],
          alternateEmails: '',
          scope: 'Global',
          sendPeriod: 'Daily',
          sendAtTime: '10:47',
          isActive: true
        });
        fetchInitialData(false);
      }
    }
  }, [isOpen, editData]);

  const fetchInitialData = async (isEdit) => {
    setLoading(true);
    try {
      // 1. Fetch Sections
      const sectionsPayload = { 
        Status: "", 
        Message: "", 
        Token: String(TOKEN), 
        Details: { UserId: ustrUserId, ManageDash: 0 } 
      };
      const sectionsRes = await axios.post(`${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_CUSTOME_DASHBOARD_LIST}`, sectionsPayload);
      
      let sectionsData = sectionsRes.data?.Details || [];
      if (typeof sectionsData === 'string') {
        try { sectionsData = JSON.parse(sectionsData); } catch (e) { }
      }
      
      const predefined = Array.isArray(sectionsData) ? sectionsData.filter(s => s.IsPredifine) : [];
      setSections(predefined);
      
      if (!isEdit && predefined.length > 0) {
         setFormData(prev => ({ ...prev, section: predefined[0].CustomDashboard }));
      }

      // 2. Fetch Users
      const usersPayload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: { ParentID: ustrUserId }
      };
      const usersRes = await axios.post(`${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.LOAD_USERS}`, usersPayload);

      let allUsers = usersRes.data?.Details || [];
      if (typeof allUsers === 'string') {
        try { allUsers = JSON.parse(allUsers); } catch (e) { }
      }
      
      if (Array.isArray(allUsers)) {
        setUsers(allUsers);
        
        if (isEdit) {
          // If in edit mode and userCollection is empty, try to match by ParentID from editData
          setFormData(prev => {
            if (prev.userCollection.length === 0 && editData?.ParentID) {
              const matched = allUsers.find(u => 
                String(u.User_ID || u.userid || u.UserId || u.User_id || '').trim() === String(editData.ParentID).trim()
              );
              
              if (matched) {
                return {
                  ...prev,
                  userCollection: [{ 
                    User_ID: matched.User_ID || matched.userid || matched.UserId || editData.ParentID, 
                    User_Login: matched.User_Login || matched.user_login || matched.UserLogin || 'User' 
                  }]
                };
              }
            }
            return prev;
          });
        } else {
          // NEW mode: Match current logged in user
          const matched = allUsers.find(u => 
            String(u.User_ID || u.userid || u.UserId || u.User_id || '').trim() === String(ustrUserId).trim()
          );
          
          if (matched) {
            setFormData(prev => ({ 
              ...prev, 
              userCollection: [{ 
                User_ID: matched.User_ID || matched.userid || matched.UserId || ustrUserId, 
                User_Login: matched.User_Login || matched.user_login || matched.UserLogin || 'User' 
              }] 
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching modal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const clearAlternateEmails = () => {
    setFormData(prev => ({ ...prev, alternateEmails: '' }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: {
          strMailSettingID: String(formData.id),
          strDashboardType: "MainDashboard",
          strSections: formData.section.toLowerCase(),
          blIsActive: formData.isActive,
          strDashboardID: "0",
          strDataDateRange: "",
          strEmailIDs: formData.alternateEmails,
          strReportID: "",
          strScope: formData.scope,
          strSendAtPeriod: "Daily",
          strSendAtTime: formData.sendAtTime,
          strSendAtWhichDay: "0",
          strUserCollections: formData.userCollection.map(u => String(u.User_ID || u.userid || u.UserId)).join(','),
          strUserID: String(ustrUserId)
        }
      };
      
      console.log('Saving mail settings with payload:', payload);
      const res = await axios.post(Save_DashBoard_Mail_Setting, payload);
      if (res.data && (res.data.Status === 1 || res.data.Status === "1")) {
        setShowSuccess(true);
      } else {
        setErrorMsg(res.data?.Message || 'Something went wrong while saving.');
      }
    } catch (error) {
      console.error('Error saving dashboard mail settings:', error);
      setErrorMsg('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    onClose();
  };

  const footer = (
    <div className="flex justify-end gap-3 w-full border-t pt-4">
      <button
        onClick={onClose}
        disabled={loading}
        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium text-sm disabled:opacity-50"
      >
        Close
      </button>
      <button
        onClick={handleSave}
        disabled={loading}
        className="px-6 py-2 bg-[#00b0f0] text-white rounded-md hover:bg-[#009bd4] transition-colors font-medium text-sm disabled:opacity-50"
      >
        {loading ? 'Saving...' : 'Save changes'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Mail setting"
      size="md"
      footer={footer}
    >
      <div className="space-y-4 py-1">
        {/* Section */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Section</label>
          <select
            name="section"
            value={formData.section}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-200 rounded text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-400 appearance-none bg-gray-50"
            style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%23a1a1aa\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center', backgroundSize: '1rem' }}
          >
            {sections.length > 0 ? sections.map(s => (
              <option key={s.Id} value={s.CustomDashboard}>{s.CustomDashboard}</option>
            )) : (
              <option value="Enquiry">Enquiry</option>
            )}
          </select>
        </div>

        {/* User Collection */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">User Collection</label>
          <div className="w-full px-3 py-2 border border-gray-200 rounded min-h-[40px] flex flex-wrap gap-2 items-center bg-white">
            {formData.userCollection.length > 0 ? formData.userCollection.map(user => (
              <div key={user.User_ID} className="flex items-center gap-1.5 bg-[#455a64] text-white px-2 py-0.5 rounded text-sm">
                <FiX className="cursor-pointer" size={14} />
                <span>{user.User_Login}</span>
              </div>
            )) : (
              <span className="text-gray-400 text-xs italic">Fetching users...</span>
            )}
          </div>
        </div>

        {/* Alternate EmailID */}
        <div className="relative">
          <div className="flex justify-between items-center mb-1.5">
            <label className="block text-sm font-bold text-gray-700 uppercase tracking-wide">Alternate EmailID</label>
            <button 
              onClick={clearAlternateEmails}
              className="p-1 text-gray-500 hover:text-[#00b0f0] transition-colors"
              title="Clear"
            >
              <FiRefreshCw size={16} className="transform rotate-45" />
            </button>
          </div>
          <textarea
            name="alternateEmails"
            value={formData.alternateEmails}
            onChange={handleChange}
            placeholder="Please type comma separated emailids"
            className="w-full px-3 py-2 border border-gray-200 rounded h-24 focus:outline-none focus:ring-1 focus:ring-blue-400 placeholder:text-gray-400 resize-none text-sm"
          />
        </div>

        {/* Scope */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Scope</label>
          <div className="flex border border-gray-200 rounded overflow-hidden">
            <div className="bg-gray-100 border-r border-gray-200 px-3 flex items-center justify-center text-gray-500">
              <FiList size={18} />
            </div>
            <select
              name="scope"
              value={formData.scope}
              onChange={handleChange}
              className="flex-1 px-3 py-2 text-gray-600 focus:outline-none focus:ring-0 appearance-none bg-white font-medium"
              style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%23a1a1aa\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center', backgroundSize: '1rem' }}
            >
              <option value="Global">Global</option>
              <option value="Self">Self</option>
            </select>
          </div>
        </div>

        {/* Send Period & Time */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Send Period</label>
            <div className="relative">
              <select
                name="sendPeriod"
                value="Daily"
                disabled
                className="w-full px-3 py-2 border border-gray-200 rounded text-gray-600 focus:outline-none focus:ring-0 appearance-none bg-white font-medium cursor-not-allowed"
                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%23a1a1aa\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center', backgroundSize: '1rem' }}
              >
                <option value="Daily">Daily</option>
              </select>
            </div>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Send at Time</label>
            <div className="relative group">
              <input
                type="time"
                name="sendAtTime"
                value={formData.sendAtTime}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 text-gray-700 font-medium"
              />
            </div>
          </div>
        </div>

        {/* Is Active */}
        <div className="flex items-center gap-3 pt-3">
          <input
            type="checkbox"
            name="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            id="isActive"
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
          />
          <label htmlFor="isActive" className="text-[13px] font-bold text-gray-700 cursor-pointer select-none">
            Is Active
          </label>
        </div>
      </div>

      {/* Success Alert */}
      <StatusAlert 
        isOpen={showSuccess}
        onClose={handleSuccessClose}
        type="success"
        title="Success"
        message="Data saved"
      />

      {/* Error Alert */}
      <StatusAlert 
        isOpen={!!errorMsg}
        onClose={() => setErrorMsg('')}
        type="error"
        title="Error"
        message={errorMsg}
      />
    </Modal>
  );
};

export default MailSettingsModal;
